import React, { useImperativeHandle, forwardRef, Ref } from 'react';
import { FormControl, FormControlProps } from 'react-bootstrap';
import { useField } from 'formik';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useEffectOnce } from 'usehooks-ts';
import { Option } from 'react-bootstrap-typeahead/types/types';

export type FormikTypeaheadOption = Option;

export type RefFormikTypeahead = {
  reset: () => void;
};

export type FormikTypeaheadProps = Omit<FormControlProps, 'onChange'> &
Omit<JSX.IntrinsicElements['input'], 'onChange'> & {
  name: string;
  options: FormikTypeaheadOption[];
  feedback?: string;
  labelKey?: string | ((option: FormikTypeaheadOption) => string);
  defaultSelectedOptions?: FormikTypeaheadOption[];
  onChange?: (selectedOptions: FormikTypeaheadOption[]) => void;
};

// eslint-disable-next-line react/display-name
const FormikTypeahead = forwardRef(
  (
    {
      name,
      options,
      labelKey,
      placeholder,
      feedback,
      defaultSelectedOptions,
      disabled,
      onChange,
    }: FormikTypeaheadProps,
    ref: Ref<RefFormikTypeahead>,
  ) => {
    const [, meta, helpers] = useField(name);
    const { error, touched } = meta;
    const isInvalid = touched && Boolean(error);
    const isValid = touched && !error;
    const className = isInvalid ? 'is-invalid' : 'is-valid';

    useEffectOnce(() => {
      (async () => {
        await setFormikFieldValue(defaultSelectedOptions ?? []);
      })();
    });

    const setFormikFieldValue = (selectedOption: FormikTypeaheadOption[]) => {
      if (labelKey && typeof labelKey === 'function') {
        return (async () => {
          await helpers.setValue(selectedOption[0] ? labelKey(selectedOption[0]) : '');
        })();
      }

      const value = labelKey
        ? (selectedOption[0] as Record<string, string | number>)?.[labelKey ]
        : selectedOption[0];

      if (value) {
        (async () => {
          await helpers.setValue(value);
        })();
      }
    };

    const handleChange = (selectedOption: FormikTypeaheadOption[]) => {
      (async () => {
        await setFormikFieldValue(selectedOption);
        onChange?.(selectedOption);
      })();
    };

    useImperativeHandle(ref, () => ({
      reset: () => {
        (async () => {
          await helpers.setValue('');
        })();
      },
    }));

    return (
      <>
        <Typeahead
          id={name}
          onChange={(selected) => handleChange(selected)}
          onInputChange={(text) => {
            (async () => {
              await helpers.setValue(text);
            })();
          }}
          onBlur={() => {
            (async () => {
              await helpers.setTouched(true);
            })();
          }}
          labelKey={labelKey}
          placeholder={placeholder}
          options={options}
          defaultSelected={defaultSelectedOptions ?? []}
          {...{ isInvalid, className, isValid, disabled }}
          inputProps={{ id: name }}
        />
        {feedback && <FormControl.Feedback type="valid">{feedback}</FormControl.Feedback>}
        {error && <FormControl.Feedback type="invalid">{error}</FormControl.Feedback>}
      </>
    );
  },
);

export { FormikTypeahead };
